import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastProvider } from './components/common/Toast'
import { ErrorBoundary, PageLoader } from './components/common/ErrorBoundary'
import { useAuthStore } from './store/auth'
import AppShell from './components/layout/AppShell'
import PushBridge from './lib/app/PushBridge'
import ScanBridge from './lib/app/ScanBridge'
import { keepNext } from './lib/next'
import TagPage from './pages/rental/TagPage'
import { useLanguage } from './i18n'
import { loadLanguage, resetLanguage } from './i18n/load'

// Lazy-loaded pages
const Login              = lazy(() => import('./pages/auth/Login'))
const SetPassword        = lazy(() => import('./pages/auth/SetPassword'))
const Landing            = lazy(() => import('./pages/auth/Landing'))
const SsoDone            = lazy(() => import('./pages/auth/SsoDone'))
const Dashboard          = lazy(() => import('./pages/dashboard/Dashboard'))
const SitesList          = lazy(() => import('./pages/sites/SitesList'))
const SiteDetail         = lazy(() => import('./pages/sites/SiteDetail'))
const MonitoringOverview = lazy(() => import('./pages/monitoring/MonitoringOverview'))
const AlertsPage         = lazy(() => import('./pages/monitoring/AlertsPage'))
const UsersList          = lazy(() => import('./pages/users/UsersList'))
const RolesList          = lazy(() => import('./pages/users/RolesList'))
const TemplatesList      = lazy(() => import('./pages/templates/TemplatesList'))
const TemplateWizard     = lazy(() => import('./pages/templates/TemplateWizard'))
const BrandingEditor     = lazy(() => import('./pages/settings/BrandingEditor'))
const CompanySettings    = lazy(() => import('./pages/settings/CompanySettings'))
const VpnPage            = lazy(() => import('./pages/settings/VpnPage'))
const ApiKeysPage        = lazy(() => import('./pages/settings/ApiKeysPage'))
const WebhooksPage       = lazy(() => import('./pages/settings/WebhooksPage'))
const MessagesPage       = lazy(() => import('./pages/settings/MessagesPage'))
const ProfilePage        = lazy(() => import('./pages/profile/ProfilePage'))
const AdminDashboard     = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminCompanies     = lazy(() => import('./pages/admin/AdminCompanies'))
const AdminSettings      = lazy(() => import('./pages/admin/AdminSettings'))
const AdminVpn           = lazy(() => import('./pages/admin/AdminVpn'))
const AdminLicenses      = lazy(() => import('./pages/admin/AdminLicenses'))
const AdminLicenseOverview = lazy(() => import('./pages/admin/AdminLicenseOverview'))
const AdminGlobalAdmins  = lazy(() => import('./pages/admin/AdminGlobalAdmins'))
const AdminModels        = lazy(() => import('./pages/admin/AdminModels'))
const AdminPushRelay     = lazy(() => import('./pages/admin/AdminPushRelay'))
const NotFoundPage          = lazy(() => import('./pages/NotFoundPage'))
const VelanoxSiteDetail     = lazy(() => import('./pages/velanox/VelanoxSiteDetail'))
const RegisterVelanoxSite   = lazy(() => import('./pages/velanox/RegisterVelanoxSite'))
const TeltonikaSiteDetail   = lazy(() => import('./pages/teltonika/TeltonikaSiteDetail'))
const RegisterTeltonikaSite = lazy(() => import('./pages/teltonika/RegisterTeltonikaSite'))
const PeplinkSiteDetail     = lazy(() => import('./pages/peplink/PeplinkSiteDetail'))
const RegisterPeplinkSite   = lazy(() => import('./pages/peplink/RegisterPeplinkSite'))
const MikrotikSiteDetail    = lazy(() => import('./pages/mikrotik/MikrotikSiteDetail'))
const RegisterMikrotikSite  = lazy(() => import('./pages/mikrotik/RegisterMikrotikSite'))
const CloudSiteDetail       = lazy(() => import('./pages/cloud/CloudSiteDetail'))
const EventSiteDetail       = lazy(() => import('./pages/sites/EventSiteDetail'))
const RegisterCloudSites    = lazy(() => import('./pages/cloud/RegisterCloudSites'))
const StarlinkPage          = lazy(() => import('./pages/starlink/StarlinkPage'))
const SiteConfigPage        = lazy(() => import('./pages/config/SiteConfigPage'))
const UnifiHostSiteDetail   = lazy(() => import('./pages/unifihost/UnifiHostSiteDetail'))
const RegisterUnifiHost     = lazy(() => import('./pages/unifihost/RegisterUnifiHost'))
const SubscriptionsPage     = lazy(() => import('./pages/notifications/SubscriptionsPage'))
const EdgePage              = lazy(() => import('./pages/edge/EdgePage'))
const RentalLayout          = lazy(() => import('./pages/rental/RentalLayout'))
const StockPage             = lazy(() => import('./pages/rental/StockPage'))
const ArticleDetail         = lazy(() => import('./pages/rental/ArticleDetail'))
const PiecePage             = lazy(() => import('./pages/rental/PiecePage'))
const CustomersPage         = lazy(() => import('./pages/rental/CustomersPage'))
const CustomerDetail        = lazy(() => import('./pages/rental/CustomersPage').then(m => ({ default: m.CustomerDetail })))
const SuppliersPage         = lazy(() => import('./pages/rental/SuppliersPage'))
const SupplierDetail        = lazy(() => import('./pages/rental/SuppliersPage').then(m => ({ default: m.SupplierDetail })))
const PlanningPage          = lazy(() => import('./pages/rental/PlanningPage'))
const EventsPage            = lazy(() => import('./pages/rental/EventsPage'))
const EventDetail           = lazy(() => import('./pages/rental/EventDetail'))
const PurchasingPage        = lazy(() => import('./pages/rental/PurchasingPage'))
const OrderDetail           = lazy(() => import('./pages/rental/PurchasingPage').then(m => ({ default: m.OrderDetail })))
const ClientViewPage        = lazy(() => import('./pages/rental/ClientViewPage'))
const ClientDevicePage      = lazy(() => import('./pages/portal/ClientDevicePage'))
const PortalShell           = lazy(() => import('./pages/portal/PortalShell'))
const PortalHome            = lazy(() => import('./pages/portal/PortalPages').then(m => ({ default: m.PortalHome })))
const PortalEvent           = lazy(() => import('./pages/portal/PortalPages').then(m => ({ default: m.PortalEvent })))
const PortalPeople          = lazy(() => import('./pages/portal/PortalPages').then(m => ({ default: m.PortalPeople })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
})

/** Home: the console in Platform administration, the company's dashboard inside one, else the landing page */
function Home() {
  const { inConsole, activeCompanyId } = useAuthStore()
  return <Navigate to={inConsole() ? '/admin' : activeCompanyId ? '/dashboard' : '/choose'} replace />
}

/** The Omada register page's old address: the page for any vendor with Omada chosen, the connection kept */
function OmadaRegister() {
  const [params] = useSearchParams()
  const connection = params.get('connection')
  return <Navigate to={`/sites/cloud/register?vendor=OMADA${connection ? `&connection=${encodeURIComponent(connection)}` : ''}`} replace />
}

/** A client person's session stays in the client view; the company's dashboard is for members */
function NotClient({ children }: { children: React.ReactNode }) {
  const client = useAuthStore(s => !!s.clientPersonId)
  return client ? <Navigate to="/portal" replace /> : <>{children}</>
}
function RequireClient({ children }: { children: React.ReactNode }) {
  const client = useAuthStore(s => !!s.clientPersonId)
  return client ? <>{children}</> : <Navigate to="/" replace />
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { identityId, accessToken } = useAuthStore()
  const location = useLocation()
  if (!identityId || !accessToken) {
    // a tag's address opened signed out: opened after the sign-in
    keepNext(location.pathname + location.search)
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function RequireGlobalAdmin({ children }: { children: React.ReactNode }) {
  const { inConsole } = useAuthStore()
  if (!inConsole()) return <Navigate to="/" replace />
  return <>{children}</>
}

/** The language in use: the server's once signed in (again after a company switch), the browser's before; <html lang> follows */
function LanguageApplier() {
  const { identityId, activeCompanyId, accessToken } = useAuthStore()
  const lang = useLanguage(s => s.lang)
  React.useEffect(() => { if (identityId && accessToken) void loadLanguage(); else resetLanguage() }, [identityId, activeCompanyId])   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    document.documentElement.lang = lang
    // texts the server sends (labels, defaults) follow the language too; a first load still under way
    // in the previous language is cancelled, else the refetch joins it and keeps its answer
    void queryClient.cancelQueries().then(() => queryClient.invalidateQueries())
  }, [lang])
  return null
}

function BrandingApplier() {
  const { branding, setBranding, activeCompanyId, fetchBranding } = useAuthStore()
  React.useEffect(() => {
    if (branding) setBranding(branding)
    if (activeCompanyId) fetchBranding(activeCompanyId)
  }, [activeCompanyId])
  return null
}

export default function App() {
  // a new language renders every page again
  useLanguage(s => s.lang)
  // Cached lists belong to one person in one company: drop them when either changes
  // (logout, login as someone else, company switch) so nobody sees the previous tenant's data.
  React.useEffect(() => useAuthStore.subscribe((s, prev) => {
    if (s.identityId !== prev.identityId || s.activeCompanyId !== prev.activeCompanyId) queryClient.clear()
  }), [])

  // Another tab logged out, logged in as someone else or switched company: follow it. This tab kept
  // showing the old company while its requests already carried the new one's token.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'velanox-auth') return
      if (!e.newValue) { window.location.href = '/login'; return }
      void useAuthStore.persist.rehydrate()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ErrorBoundary>
          <BrandingApplier />
          <LanguageApplier />
          <BrowserRouter>
            <PushBridge />
            <ScanBridge />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/ga-login" element={<Navigate to="/login" replace />} />
                <Route path="/auth/set-password" element={<SetPassword />} />
                <Route path="/choose" element={<RequireAuth><Landing /></RequireAuth>} />
                <Route path="/select-company" element={<Navigate to="/choose" replace />} />
                <Route path="/sso" element={<SsoDone />} />

                {/* A rental customer's client view */}
                <Route path="/portal" element={<RequireAuth><RequireClient><PortalShell /></RequireClient></RequireAuth>}>
                  <Route index element={<PortalHome />} />
                  <Route path="events/:id" element={<PortalEvent />} />
                  {/* a device out on the event, read-only */}
                  <Route path="events/:id/devices/:itemId" element={<ClientDevicePage />} />
                  <Route path="people" element={<PortalPeople />} />
                  {/* the person's own account: password, two-factor sign-in, sessions, language */}
                  <Route path="profile" element={<ProfilePage />} />
                </Route>

                {/* Protected app routes */}
                <Route path="/" element={<RequireAuth><NotClient><AppShell /></NotClient></RequireAuth>}>
                  <Route index element={<Home />} />
                  <Route path="dashboard" element={<Dashboard />} />

                  {/* Monitoring */}
                  <Route path="monitoring" element={<MonitoringOverview />} />
                  <Route path="monitoring/alerts" element={<AlertsPage />} />

                  {/* Sites */}
                  <Route path="sites" element={<SitesList />} />
                  <Route path="sites/:siteId" element={<SiteDetail />} />
                  <Route path="sites/:siteId/velanox" element={<VelanoxSiteDetail />} />
                  <Route path="sites/velanox/register" element={<RegisterVelanoxSite />} />
                  <Route path="sites/:siteId/teltonika" element={<TeltonikaSiteDetail />} />
                  <Route path="sites/teltonika/register" element={<RegisterTeltonikaSite />} />
                  <Route path="sites/:siteId/peplink" element={<PeplinkSiteDetail />} />
                  <Route path="sites/peplink/register" element={<RegisterPeplinkSite />} />
                  <Route path="sites/:siteId/mikrotik" element={<MikrotikSiteDetail />} />
                  <Route path="sites/mikrotik/register" element={<RegisterMikrotikSite />} />
                  <Route path="sites/:siteId/cloud" element={<CloudSiteDetail />} />
                  <Route path="sites/:siteId/event" element={<EventSiteDetail />} />
                  <Route path="sites/cloud/register" element={<RegisterCloudSites />} />
                  <Route path="sites/omada/register" element={<OmadaRegister />} />
                  <Route path="sites/starlink" element={<StarlinkPage />} />
                  <Route path="sites/:siteId/config" element={<SiteConfigPage />} />
                  <Route path="sites/:siteId/unifi-host" element={<UnifiHostSiteDetail />} />
                  <Route path="sites/unifi-host/register" element={<RegisterUnifiHost />} />

                  {/* Rental */}
                  <Route path="rental" element={<RentalLayout />}>
                    <Route index element={<Navigate to="planning" replace />} />
                    <Route path="planning" element={<PlanningPage />} />
                    <Route path="events" element={<EventsPage />} />
                    <Route path="events/:id" element={<EventDetail />} />
                    <Route path="events/:id/client-view" element={<ClientViewPage />} />
                    <Route path="events/:id/client-view/devices/:itemId" element={<ClientDevicePage preview />} />
                    <Route path="purchasing" element={<PurchasingPage />} />
                    <Route path="purchasing/:kind/:id" element={<OrderDetail />} />
                    <Route path="stock" element={<StockPage />} />
                    <Route path="stock/articles/:id" element={<ArticleDetail />} />
                    <Route path="stock/pieces/:id" element={<PiecePage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="customers/:id" element={<CustomerDetail />} />
                    <Route path="suppliers" element={<SuppliersPage />} />
                    <Route path="suppliers/:id" element={<SupplierDetail />} />
                  </Route>
                  {/* Users */}
                  <Route path="users" element={<UsersList />} />
                  <Route path="users/roles" element={<RolesList />} />

                  {/* Templates */}
                  <Route path="templates" element={<TemplatesList />} />
                  <Route path="templates/new" element={<TemplateWizard />} />
                  <Route path="templates/:templateId/edit" element={<TemplateWizard />} />

                  {/* Settings */}
                  <Route path="settings/branding" element={<BrandingEditor />} />
                  <Route path="settings/company" element={<CompanySettings />} />
                  <Route path="settings/vpn" element={<VpnPage />} />
                  <Route path="settings/api-keys" element={<ApiKeysPage />} />
                  <Route path="settings/webhooks" element={<WebhooksPage />} />
                  <Route path="settings/messages" element={<MessagesPage />} />

                  {/* Profile */}
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="notifications" element={<SubscriptionsPage />} />
                  {/* a tag's address (written on NFC tags by the app) */}
                  <Route path="t/:code" element={<TagPage />} />

                  {/* Global admin */}
                  <Route path="admin" element={<RequireGlobalAdmin><AdminDashboard /></RequireGlobalAdmin>} />
                  <Route path="admin/companies" element={<RequireGlobalAdmin><AdminCompanies /></RequireGlobalAdmin>} />
                  <Route path="admin/settings" element={<RequireGlobalAdmin><AdminSettings /></RequireGlobalAdmin>} />
                  <Route path="admin/vpn" element={<RequireGlobalAdmin><AdminVpn /></RequireGlobalAdmin>} />
                  <Route path="admin/edge" element={<RequireGlobalAdmin><EdgePage /></RequireGlobalAdmin>} />
                  <Route path="admin/licenses" element={<RequireGlobalAdmin><AdminLicenses /></RequireGlobalAdmin>} />
                  <Route path="admin/license-overview" element={<RequireGlobalAdmin><AdminLicenseOverview /></RequireGlobalAdmin>} />
                  <Route path="admin/global-admins" element={<RequireGlobalAdmin><AdminGlobalAdmins /></RequireGlobalAdmin>} />
                  <Route path="admin/models" element={<RequireGlobalAdmin><AdminModels /></RequireGlobalAdmin>} />
                  <Route path="admin/push-relay" element={<RequireGlobalAdmin><AdminPushRelay /></RequireGlobalAdmin>} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ErrorBoundary>
      </ToastProvider>
    </QueryClientProvider>
  )
}
